import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'shared/helpers/dayjs';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Box, CircularProgress } from '@mui/material';
import { Time } from 'icons/untitledui';

// components
import DateSelector from 'teamLeadInsights/components/DateSelector';
import ReportContent from 'teamLeadInsights/components/ReportContent';
import { SelectionDropdown } from 'shared/components/dropdown/SelectionDropdown';

// helpers
import parseReportDateParam from 'teamLeadInsights/helpers/parseReportDateParam';

// hooks
import { useSiteShifts } from 'shared/hooks/api/shiftNotes';
import { useTeamSites } from 'dashboard/hooks/useTeamSites';

// styles
import * as FiltersStyles from 'dashboard/styles/Filters.style';
import * as Text from 'theme/components/Text.style';
import * as ViewTeamLeadStyles from 'teamLeadInsights/styles/ViewTeamLead.style';

export default function SiteReport(): JSX.Element {
  const { t } = useTranslation('pageTeamLeadInsights');

  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState<Date | null>(() =>
    parseReportDateParam(location.search)
  );
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const siteParam = new URLSearchParams(location.search).get('site');

  const { data: sites = [], isLoading: sitesLoading } = useTeamSites();
  const { data: siteShifts = [], isLoading: siteShiftsLoading } =
    useSiteShifts(selectedDate);
  const isLoadingShifts = sitesLoading || siteShiftsLoading;

  const siteOptions = useMemo(
    () =>
      sites.map((site) => {
        const shift = siteShifts.find((s) => s.site === site.name);
        const noteCount = shift ? shift.shift_notes.length : null;

        return {
          value: site.id,
          label: site.name,
          description:
            noteCount === null || noteCount === 0
              ? t('viewTeamLead.NoSummary')
              : t('viewTeamLead.SummaryOfNotes', { count: noteCount }),
        };
      }),
    [sites, siteShifts, t]
  );

  const selectedSite = sites.find((s) => s.id === selectedSiteId);
  const selectedSiteShift = siteShifts.find(
    (s) => s.site === selectedSite?.name
  );

  useEffect(() => {
    if (selectedSiteId || sites.length === 0) return;
    const siteFromUrl = sites.find((site) => site.id === siteParam);
    setSelectedSiteId(siteFromUrl?.id ?? sites[0].id);
  }, [sites, selectedSiteId, siteParam]);

  return (
    <ViewTeamLeadStyles.Section>
      <ViewTeamLeadStyles.PageHeader>
        <Text.H5>{t('viewTeamLead.ShiftReports')}</Text.H5>
        <ViewTeamLeadStyles.FiltersRow>
          <DateSelector
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />
          <FiltersStyles.FilterWrapper>
            <SelectionDropdown
              ariaLabel={t('viewTeamLead.SelectSite')}
              value={selectedSiteId}
              options={siteOptions}
              onChange={setSelectedSiteId}
              placeholder={t('viewTeamLead.SelectSite')}
              fullWidth
            />
          </FiltersStyles.FilterWrapper>
        </ViewTeamLeadStyles.FiltersRow>
      </ViewTeamLeadStyles.PageHeader>

      {isLoadingShifts && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoadingShifts && !selectedSiteShift && selectedSite && (
        <ViewTeamLeadStyles.NoShiftsContainer>
          <ViewTeamLeadStyles.NoShiftsIcon>
            <Time.CalenderDate size={32} />
          </ViewTeamLeadStyles.NoShiftsIcon>
          <Text.H5>{t('viewTeamLead.NoShiftReportsFound')}</Text.H5>
          <Text.P2 style={{ textAlign: 'center' }}>
            {t('viewTeamLead.NoShiftReportsMessage')}
          </Text.P2>
        </ViewTeamLeadStyles.NoShiftsContainer>
      )}

      {!isLoadingShifts && selectedSiteShift && (
        <ViewTeamLeadStyles.SiteCard>
          <ViewTeamLeadStyles.SiteHeader>
            <ViewTeamLeadStyles.SiteHeaderTitle>
              <Text.H5>{selectedSiteShift.site}</Text.H5>
              <Text.P2Semibold>
                {selectedDate
                  ? dayjs(selectedDate).format('MMMM D, YYYY')
                  : t('viewTeamLead.TodaysReports')}
              </Text.P2Semibold>
            </ViewTeamLeadStyles.SiteHeaderTitle>
          </ViewTeamLeadStyles.SiteHeader>

          <ReportContent siteId={selectedSiteShift.id} />
        </ViewTeamLeadStyles.SiteCard>
      )}
    </ViewTeamLeadStyles.Section>
  );
}
